import { Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';

/** One instance per lesson row — both the single and multiple layouts in
 * LiveLessonCountdownComponent need independently-ticking timers, so this is its own
 * component rather than a shared method. Runs its own 1s interval rather than reusing
 * CurrentTimeService (which only ticks every 10s — fine for "is this live/upcoming" checks
 * elsewhere, but would make the SECS digit visibly jump/stutter here). */
@Component({
  selector: 'app-live-lesson-countdown-timer',
  standalone: true,
  templateUrl: './live-lesson-countdown-timer.component.html',
  styleUrl: './live-lesson-countdown-timer.component.scss',
})
export class LiveLessonCountdownTimerComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  targetDate = input.required<string>();
  // The multiple-lessons layout renders two of these stacked in the same card, so each gets a
  // smaller tile/font than the single-lesson layout's one big countdown (matches Figma: 91px
  // digit/177.868px tile vs 108px/204px at the design's 4x scale).
  compact = input(false);
  // The lesson-detail page's "Lesson summary" banner uses purple-gradient tiles with white
  // digits (matches its own purple/white badge), instead of the white-tile/dark-digit look
  // everywhere else this component is used.
  inverted = input(false);

  private msRemaining = signal(0);

  days = computed(() => this.pad(Math.floor(this.msRemaining() / (1000 * 60 * 60 * 24))));
  hours = computed(() => this.pad(Math.floor((this.msRemaining() / (1000 * 60 * 60)) % 24)));
  mins = computed(() => this.pad(Math.floor((this.msRemaining() / (1000 * 60)) % 60)));
  secs = computed(() => this.pad(Math.floor((this.msRemaining() / 1000) % 60)));

  ngOnInit(): void {
    this.updateRemaining();
    const interval = setInterval(() => this.updateRemaining(), 1000);
    this.destroyRef.onDestroy(() => clearInterval(interval));
  }

  private updateRemaining(): void {
    const diff = new Date(this.targetDate()).getTime() - Date.now();
    this.msRemaining.set(Math.max(diff, 0));
  }

  private pad(value: number): string {
    return String(value).padStart(2, '0');
  }
}
